import { isRouteErrorResponse, useRouteError } from 'react-router';
import { IconAlert } from './icons';
import { Button, Container, Panel } from './ui';

/**
 * What a page shows when it throws while loading or rendering. Without this,
 * React Router's bare "Unexpected Application Error!" replaced the whole site.
 * `full` is the last-resort version, used when the layout itself failed.
 */
export function RouteError({ full }: { full?: boolean }) {
  const err = useRouteError();
  // After a deploy, a tab opened earlier asks for page chunks that no longer
  // exist. A reload fetches the new ones.
  const stale = err instanceof Error && /dynamically imported module|Importing a module script failed|error loading dynamically/i.test(err.message);
  const notFound = isRouteErrorResponse(err) && err.status === 404;

  const body = (
    <Container className="py-16 sm:py-24">
      <Panel inner="p-8 text-center sm:p-10">
        <div className="flex justify-center text-danger">
          <IconAlert size={32} />
        </div>
        <h1 className="mt-4 font-display text-2xl tracking-wide text-ink">
          {stale ? 'The site was just updated' : notFound ? 'Page not found' : 'Something went wrong'}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-muted">
          {stale ? 'Reload to get the new version.' : 'Try again in a moment. If it keeps happening, tell the staff on Discord what you were doing.'}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button onClick={() => window.location.reload()}>{stale ? 'Reload' : 'Try again'}</Button>
          <a href="/" className="inline-flex h-11 items-center border border-line px-5 text-sm font-semibold uppercase tracking-[0.12em] text-ink hover:border-gold">
            Back to home
          </a>
        </div>
      </Panel>
    </Container>
  );
  return full ? <div className="min-h-dvh bg-ground">{body}</div> : body;
}
